import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { categoriesLoad } from '../../../../actions/category';
import {
  loadProductDrafts,
  loadProductList,
  productDefault
} from '../../../../actions/product';
import {
  filterProducts, getProductFilterOptions, getProductStock,
  groupProductsBySection, normalizeProductText, sortProducts
} from '../../../../helpers/admin/productListHelper';

import ProductModal from './ProductModal';
import ProductListFilters from './ProductListFilters';
import ProductListItem from './ProductListItem';
import ProductListMenu from './ProductListMenu';
import ProductListSkeleton from './ProductListSkeleton';
import ProductDraftList from './ProductDraftList';
import VariantModal from './VariantList/VariantModal';
import VariantDrawer from './VariantList/VariantDrawer';

import {
  Alert, Box, Button, Collapse, Container, Grid, InputAdornment,
  TextField, Typography, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const AddButton = styled(Button)(() => ({
  color: '#fff',
  backgroundColor: '#465fff',
  textTransform: 'none',
  height: '45px',
  '&:hover': { backgroundColor: '#2e47e6' }
}));

const SECTION_CONFIG = [
  { key: 'ownBrands', title: 'Marcas Nefrópolis' },
  { key: 'thirdParty', title: 'Productos de terceros' },
  { key: 'medicines', title: 'Medicamentos' },
  { key: 'refrigerated', title: 'Productos fríos' }
];

const ARCHIVED_SECTION = {
  key: 'archived',
  title: 'Productos eliminados',
  special: true
};

const initialFilters = {
  search: '', brands: [], categories: [], minPrice: '', maxPrice: '',
  inventory: 'all', visibility: 'all'
};

const SectionHeader = ({ title, subtitle, open, onToggle, special = false }) => <Box sx={{
  alignItems: 'center',
  backgroundColor: special ? '#fff4f4' : '#fff',
  border: `1px solid ${special ? '#d97979' : '#e5e7eb'}`,
  borderRadius: 2,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1.5,
  justifyContent: 'space-between',
  mb: 2,
  p: 2
}}>
  <Box>
    <Typography color={special ? 'error.dark' : 'text.primary'} variant="h6">
      {title}
    </Typography>
    <Typography color="text.secondary" variant="body2">{subtitle}</Typography>
  </Box>
  <Button
    color={special ? 'error' : 'primary'}
    endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    onClick={onToggle}
    variant="outlined"
  >
    {open ? 'Ocultar' : 'Mostrar'}
  </Button>
</Box>;

const ProductSection = ({ section, products, open, onToggle, anchorEl, setAnchorEl }) => {
  const stock = products.reduce((total, product) => total + getProductStock(product), 0);

  return <Box sx={{ mb: 4 }}>
    <SectionHeader
      title={section.title}
      subtitle={`${products.length} productos · ${stock} existencias`}
      open={open}
      onToggle={onToggle}
      special={section.special}
    />
    <Collapse in={open} timeout="auto" unmountOnExit>
      {products.length === 0 ? <Alert severity="info">
        No hay productos que coincidan en esta sección.
      </Alert> : <Grid container spacing={4}>
        {products.map(product => <ProductListItem
          key={product._id}
          product={product}
          sectionKey={section.key}
          archived={section.special}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />)}
      </Grid>}
    </Collapse>
  </Box>;
};

const ProductList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    products,
    drafts,
    draftsLoading,
    activeVariants,
    listLoading
  } = useSelector(state => state.product);
  const { prod_id, title, variants } = activeVariants;
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState('name-asc');
  const [openSections, setOpenSections] = useState({
    ownBrands: true,
    thirdParty: true,
    medicines: true,
    refrigerated: true,
    drafts: true,
    archived: true
  });
  const options = useMemo(() => getProductFilterOptions(products), [products]);
  const sections = useMemo(() => groupProductsBySection(
    sortProducts(filterProducts(products, filters), sort)
  ), [products, filters, sort]);
  const filteredDrafts = useMemo(() => {
    const search = normalizeProductText(filters.search);
    if (!search)
      return drafts;
    return drafts.filter(draft => [
      draft.title,
      draft.brand,
      ...(draft.variants || []).map(variant => variant.variant_title)
    ].some(value => normalizeProductText(value).includes(search)));
  }, [drafts, filters.search]);

  useEffect(() => {
    dispatch(productDefault());
    dispatch(loadProductList());
    dispatch(loadProductDrafts());
    dispatch(categoriesLoad());
  }, [dispatch]);

  return <>
    <Container maxWidth="xxxl" sx={{ mt: 12, mb: 5 }}>
      <Typography variant="h5" sx={{ fontWeight: '600' }} gutterBottom>
        Productos
      </Typography>
      <AddButton onClick={() => navigate('/nefro-admin/producto-agregar')}
        variant="contained" sx={{ mb: 3 }} startIcon={<AddIcon />}>
        Añadir producto
      </AddButton>
      <TextField
        variant="outlined"
        value={filters.search}
        onChange={({ target }) => setFilters(current => ({ ...current, search: target.value }))}
        placeholder="Buscar por producto, variante, categoría o marca..."
        fullWidth
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        InputLabelProps={{ shrink: false }}
        sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 }, mb: 2 }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
          <InputLabel id="product-sort">Ordenar por</InputLabel>
          <Select labelId="product-sort" value={sort} label="Ordenar por"
            onChange={({ target }) => setSort(target.value)}>
            <MenuItem value="name-asc">Nombre: A–Z</MenuItem>
            <MenuItem value="name-desc">Nombre: Z–A</MenuItem>
            <MenuItem value="stock-asc">Existencias: menor a mayor</MenuItem>
            <MenuItem value="stock-desc">Existencias: mayor a menor</MenuItem>
            <MenuItem value="price-asc">Precio: menor a mayor</MenuItem>
            <MenuItem value="price-desc">Precio: mayor a menor</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <ProductListFilters filters={filters} options={options} onChange={setFilters} />
      {listLoading ? <ProductListSkeleton /> : products.length === 0
        && drafts.length === 0
        && !draftsLoading ? <Alert severity="info">
        No tiene productos en tienda.
      </Alert> : <>
        {SECTION_CONFIG.map(section => <ProductSection
          key={section.key}
          section={section}
          products={sections[section.key]}
          open={openSections[section.key]}
          onToggle={() => setOpenSections(current => ({
            ...current, [section.key]: !current[section.key]
          }))}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />)}
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            title="Borradores"
            subtitle={`${filteredDrafts.length} borradores · vigencia de 30 días`}
            open={openSections.drafts}
            onToggle={() => setOpenSections(current => ({
              ...current,
              drafts: !current.drafts
            }))}
          />
          <Collapse in={openSections.drafts} timeout="auto" unmountOnExit>
            {draftsLoading
              ? <ProductListSkeleton />
              : <ProductDraftList drafts={filteredDrafts} />}
          </Collapse>
        </Box>
        <ProductSection
          section={ARCHIVED_SECTION}
          products={sections.archived}
          open={openSections.archived}
          onToggle={() => setOpenSections(current => ({
            ...current,
            archived: !current.archived
          }))}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />
      </>}
    </Container>

    <ProductModal />
    <VariantModal />
    <ProductListMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl}
      setOpenDrawer={setOpenDrawer} />
    <VariantDrawer
      openDrawer={openDrawer}
      setOpenDrawer={setOpenDrawer}
      product={{ prod_id, title }}
      variants={variants}
      prod_id={prod_id}
      title={title}
    />
  </>;
};

export default ProductList;
